import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { User, AuthResponse, UpdateProfileDto } from '@/lib/types';

export function useLogin() {
  return useMutation({
    mutationFn: async (phone: string): Promise<{ message: string }> => {
      const response = await apiClient.post('/auth/login', { phone });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to send OTP');
      }
      return response.json();
    },
  });
}

export function useVerifyOtp() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ phone, otpCode }: { phone: string; otpCode: string }): Promise<AuthResponse> => {
      const response = await apiClient.post('/auth/verify-otp', { phone, otpCode });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Invalid OTP');
      }
      const data: AuthResponse = await response.json();
      apiClient.setToken(data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/auth/profile'] });
    },
  });
}

export function useResendOtp() {
  return useMutation({
    mutationFn: async (phone: string): Promise<{ message: string }> => {
      const response = await apiClient.post('/auth/resend-otp', { phone });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to resend OTP');
      }
      return response.json();
    },
  });
}

export function useProfile() {
  return useQuery({
    queryKey: ['/auth/profile'],
    queryFn: async (): Promise<User | null> => {
      const token = apiClient.getToken();
      if (!token) return null;
      
      const response = await apiClient.get('/auth/profile');
      if (!response.ok) {
        if (response.status === 401) return null;
        throw new Error('Failed to fetch profile');
      }
      return response.json();
    },
    enabled: !!apiClient.getToken(),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: UpdateProfileDto): Promise<User> => {
      const response = await apiClient.post('/auth/update-profile', data);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update profile');
      }
      const user = await response.json();
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/auth/profile'] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  
  return () => {
    apiClient.clearToken();
    queryClient.clear();
  };
}

export function useIsAuthenticated(): boolean {
  return !!apiClient.getToken();
}

export function getCurrentUser(): User | null {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}
